import type { GraphQLTypes, InputType } from '@zeus/index'
import { $ } from '@zeus/index'
import { typedGql } from '@zeus/typedDocumentNode'
import { trackingStatsFields } from '~/gql/scorm/scorm'
import { scalars } from '~/gql/utils/scalars'

const moduleFields = {
  id: true,
  title: true,
  description: true,
  scormVersion: true,
  launchUrl: true,
  status: true,
  // Durée estimée et seuil de réussite (#74). Le premier dit à l'apprenant s'il
  // en a pour dix minutes AVANT de cliquer ; le second transforme un score nu
  // en verdict — « 82/100 » ne dit pas si c'est réussi, « 82/100, seuil 70 » si.
  durationMinutes: true,
  masteryScore: true,
} as const

const sessionFields = {
  id: true,
  name: true,
  status: true,
  startDate: true,
  endDate: true,
  progressionMode: true,
  // Ce qu'exige l'attestation (#10) ; le seuil sur [0..1] pour `SCORE`.
  certificateCondition: true,
  certificateMinScore: true,
  // Modèle d'attestation choisi (#162) ; absent = celui par défaut.
  certificateTemplateId: true,
  // ⚠️ Le PREMIER module du parcours, le temps que les derniers écrans passent
  // à `modules`. Ne pas s'appuyer dessus dans du code neuf.
  module: moduleFields,
  modules: {
    id: true,
    position: true,
    unlockAt: true,
    unlocked: true,
    lockReason: true,
    prerequisiteModuleId: true,
    prerequisiteMinScore: true,
    allowRetake: true,
    scoreRetention: true,
    sectionId: true,
    module: moduleFields,
  },
  // Chapitres du parcours (#75), dans l'ordre : hérités par la vue apprenant.
  sections: { id: true, title: true, position: true },
  // Fiche formation (#319) : ce que l'apprenant doit savoir, éditée en back-office.
  objectives: true,
  prerequisites: true,
  audience: true,
  teachingMethods: true,
  assessmentMethods: true,
  accessibility: true,
  contact: true,
  // Positionnement d'entrée exigé (#321).
  positioningEnabled: true,
  enrolledUsers: {
    id: true,
    email: true,
    firstName: true,
    lastName: true,
  },
} as const

/**
 * La vue de gestion : celle de l'apprenant, plus les étiquettes (#29), qui
 * n'ont pas à sortir du back-office.
 */
const managedSessionFields = {
  ...sessionFields,
  tags: { id: true, name: true },
  // Qui anime le parcours (#244) ; pas encore montré aux apprenants.
  trainers: { id: true, email: true, firstName: true, lastName: true, role: true },
} as const

const trackingFields = {
  completionStatus: true,
  successStatus: true,
  scoreRaw: true,
  scoreMax: true,
  completedAt: true,
  // Temps réellement passé, en secondes (#74). Converti côté API à partir de la
  // chaîne SCORM : un second analyseur de ce format côté front finirait par ne
  // plus annoncer la même durée que l'attestation, qui lit déjà celui-là.
  totalTimeSeconds: true,
  // Dernière trace d'activité, y compris sur un module jamais terminé : c'est
  // elle qui dit à l'accueil apprenant quel parcours proposer de reprendre
  // (`learnerFocus`). `completedAt` ne saurait pas le faire — il reste nul tant
  // que le module n'est pas allé au bout.
  updatedAt: true,
} as const

/**
 * Vue portant sur UN apprenant : à `sessionFields` s'ajoutent l'avancement du
 * parcours et, sur chaque module, son état d'ouverture et la progression.
 *
 * Ces champs sont nuls dans la vue admin (`sessions`), qui ne vise personne en
 * particulier — d'où la sélection séparée plutôt qu'un `sessionFields` gonflé
 * de champs toujours vides pour la moitié des appelants.
 */
const learnerSessionFields = {
  ...sessionFields,
  modules: {
    ...sessionFields.modules,
    myTracking: trackingFields,
  },
  progress: {
    totalModules: true,
    completedModules: true,
    completed: true,
  },
  // L'attestation, évaluée par l'API seule (#10) : l'écran ne refait pas la règle.
  certificate: { unlocked: true, reason: true, blockingModuleIds: true },
  // Depuis quand on attend cette personne sur cette formation — et non depuis
  // quand le parcours existe, que `createdAt` dirait à tort.
  myEnrolledAt: true,
  myTracking: trackingFields,
  // Le positionnement de l'apprenant (#321) : l'entrée à remplir, la sortie proposée.
  myPositioning: { required: true, entryPending: true, entryDone: true, exitDone: true },
  // Les documents du parcours (#326), et ce que l'apprenant en a déjà lu.
  documents: { id: true, title: true, requiredBeforeStart: true, acknowledgedAt: true },
} as const

export const GQL_SESSIONS = typedGql('query', { scalars })({
  sessions: managedSessionFields,
})

export type StoredSession = InputType<
  GraphQLTypes['SessionModel'],
  typeof managedSessionFields,
  typeof scalars
>

/** La fiche session (#243) : la vue de gestion, plus sa date de création. */
const sessionDetailFields = {
  ...managedSessionFields,
  createdAt: true,
} as const

export const GQL_SESSION = typedGql('query', { scalars })({
  session: [{ id: $('id', 'ID!') }, sessionDetailFields],
})

export type SessionDetail = InputType<
  GraphQLTypes['SessionModel'],
  typeof sessionDetailFields,
  typeof scalars
>

const sessionResultsFields = {
  stats: trackingStatsFields,
  participants: {
    userId: true,
    firstName: true,
    lastName: true,
    email: true,
    groups: true,
    state: true,
    completedModules: true,
    totalModules: true,
    averageScorePercent: true,
    timeSpentSeconds: true,
    lastActivityAt: true,
    // Abandon déclaré par l'organisme (#324).
    droppedAt: true,
    dropReason: true,
    dropNote: true,
  },
  modules: { moduleId: true, completedCount: true },
} as const

/** Résultats d'une session (#243) : compteurs du tableau de bord, inscrits, modules. */
export const GQL_SESSION_RESULTS = typedGql('query', { scalars })({
  sessionResults: [{ sessionId: $('sessionId', 'ID!') }, sessionResultsFields],
})

export type SessionResultsData = InputType<
  GraphQLTypes['SessionResults'],
  typeof sessionResultsFields,
  typeof scalars
>

export type SessionParticipant = SessionResultsData['participants'][number]

export const GQL_MY_SESSIONS = typedGql('query', { scalars })({
  mySessions: learnerSessionFields,
})

export const GQL_USER_SESSIONS = typedGql('query', { scalars })({
  userSessions: [{ userId: $('userId', 'ID!') }, learnerSessionFields],
})

export const GQL_CREATE_SESSION = typedGql('mutation', { scalars })({
  createSession: [{ input: $('input', 'CreateSessionInput!') }, managedSessionFields],
})

export const GQL_UPDATE_SESSION = typedGql('mutation', { scalars })({
  updateSession: [
    { id: $('id', 'ID!'), input: $('input', 'UpdateSessionInput!') },
    managedSessionFields,
  ],
})

/** Copie la configuration d'une session pour un nouveau groupe, sans dates ni apprenants (plan 062). */
export const GQL_DUPLICATE_SESSION = typedGql('mutation', { scalars })({
  duplicateSession: [{ id: $('id', 'ID!'), name: $('name', 'String!') }, managedSessionFields],
})

export const GQL_DELETE_SESSION = typedGql('mutation')({
  deleteSession: [{ id: $('id', 'ID!') }, true],
})
